import { useEffect, useRef, type KeyboardEvent } from "react";
import type { LucideIcon } from "lucide-react";

export type V3PageTab = {
  value: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
  disabled?: boolean;
  blocked?: boolean;
  blockedLabel?: string;
  panelId?: string;
};

function isUnavailable(tab: V3PageTab): boolean {
  return Boolean(tab.disabled || tab.blocked);
}

/**
 * V3 正式页内导航。
 * - 只切换当前页面内的业务视图，不改变浏览器式主工作区标签。
 * - 支持 tablist/tab 语义、方向键、Home/End、disabled/blocked 与窄屏横向滚动。
 */
export function V3PageTabs({ tabs, active, onChange, ariaLabel = "页内视图", className = "" }: {
  tabs: V3PageTab[];
  active: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  className?: string;
}) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    const index = tabs.findIndex((tab) => tab.value === active);
    const activeTab = tabRefs.current[index];
    if (typeof activeTab?.scrollIntoView === "function") activeTab.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active, tabs]);

  function enabledIndexes(): number[] {
    return tabs.map((tab, index) => ({ tab, index })).filter(({ tab }) => !isUnavailable(tab)).map(({ index }) => index);
  }

  function activate(index: number) {
    const tab = tabs[index];
    if (!tab || isUnavailable(tab)) return;
    tabRefs.current[index]?.focus();
    onChange(tab.value);
  }

  function moveFrom(index: number, direction: 1 | -1) {
    const enabled = enabledIndexes();
    if (enabled.length === 0) return;
    const currentEnabledIndex = enabled.includes(index) ? enabled.indexOf(index) : Math.max(0, enabled.findIndex((item) => tabs[item]?.value === active));
    const nextPosition = currentEnabledIndex < 0 ? 0 : (currentEnabledIndex + direction + enabled.length) % enabled.length;
    activate(enabled[nextPosition]!);
  }

  function moveToEdge(edge: "first" | "last") {
    const enabled = enabledIndexes();
    const index = edge === "first" ? enabled[0] : enabled[enabled.length - 1];
    if (index !== undefined) activate(index);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      moveFrom(index, 1);
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveFrom(index, -1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      moveToEdge("first");
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      moveToEdge("last");
    }
  }

  return <nav className={`v3-page-tabs ${className}`.trim()} aria-label={ariaLabel}>
    <div className="v3-page-tabs-scroll">
      <div className="v3-page-tabs-list" role="tablist" aria-label={ariaLabel}>
        {tabs.map((tab, index) => {
          const selected = tab.value === active;
          const unavailable = isUnavailable(tab);
          const panelId = tab.panelId ?? `v3-page-tab-panel-${tab.value}`;
          const Icon = tab.icon;
          return <button
            key={tab.value}
            ref={(node) => { tabRefs.current[index] = node; }}
            type="button"
            role="tab"
            id={`v3-page-tab-${tab.value}`}
            aria-controls={panelId}
            aria-selected={selected}
            aria-disabled={unavailable || undefined}
            className={`v3-page-tab ${selected ? "active" : ""} ${tab.disabled ? "disabled" : ""} ${tab.blocked ? "blocked" : ""}`.trim()}
            disabled={unavailable}
            tabIndex={selected && !unavailable ? 0 : -1}
            onClick={() => activate(index)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {Icon && <Icon size={16} aria-hidden />}
            <span className="v3-page-tab-label">{tab.label}</span>
            {tab.count !== undefined && <span className="v3-page-tab-count app-number" aria-label={`${tab.label}数量 ${tab.count}`}>{tab.count}</span>}
            {tab.blocked && <span className="v3-page-tab-blocked">{tab.blockedLabel ?? "待接入"}</span>}
          </button>;
        })}
      </div>
    </div>
  </nav>;
}
